import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, Info, Rocket, Zap, ShieldCheck, Play, Tv, Wifi, Smartphone, HelpCircle, Monitor, Clock, Settings } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Como Começar a Utilizar IPTV Hoje", href: "/blog/como-comecar-utilizar-iptv-hoje" }
  ];

  const faq = [
    {
      question: "Consigo instalar em menos de 10 minutos?",
      answer: "Sim! Se você já tiver o aparelho conectado à internet, o processo de baixar o app e colocar o login leva menos de 5 minutos com o nosso guia rápido."
    },
    {
      question: "Preciso de cartão de crédito para testar?",
      answer: "Na P2 PRO não. Oferecemos o teste totalmente gratuito e sem compromisso para você validar a qualidade antes de qualquer investimento."
    },
    {
      question: "O suporte ajuda na instalação?",
      answer: "Com certeza. Temos uma equipe de prontidão no WhatsApp que pode até fazer a configuração remotamente para você em certos modelos de TV."
    }
  ];

  const benefits = [
    {
      title: "Praticidade Imediata",
      description: "Saia da leitura deste artigo direto para o sofá com todos os canais liberados.",
      icon: <Clock className="w-6 h-6" />
    },
    {
      title: "Instalação Guiada",
      description: "Passo a passo visual que elimina qualquer chance de erro durante a configuração.",
      icon: <Settings className="w-6 h-6" />
    },
    {
      title: "Satisfação Instantânea",
      description: "A alegria de ver seu conteúdo favorito em alta definição sem precisar esperar o técnico.",
      icon: <Zap className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "Solicite o Acesso",
      description: "Clique no botão de WhatsApp e peça seu teste gratuito agora."
    },
    {
      title: "Identifique seu App",
      description: "Nossa equipe dirá qual o melhor player para o seu modelo de TV."
    },
    {
      title: "Insira as Credenciais",
      description: "Coloque o usuário e senha que enviaremos no seu chat."
    },
    {
      title: "Organize sua Grade",
      description: "Favorite seus canais e comece a maratona de entretenimento."
    }
  ];

  const diferenciais = [
    {
      title: "Ativação em Segundos",
      description: "Sistema automatizado que libera seu sinal assim que o teste é solicitado.",
      icon: <Rocket className="w-6 h-6" />
    },
    {
      title: "Apps de Alta Velocidade",
      description: "Players otimizados que abrem canais instantaneamente sem 'loading' infinito.",
      icon: <Play className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. A Decisão Que Vai Mudar Sua TV em Minutos",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Você está cansado de pagar caro por uma grade de canais que não assiste? Quer saber <strong>como começar a utilizar IPTV hoje</strong> sem complicação e sem burocracia? A boa notícia é que você está a poucos cliques de distância de uma revolução no seu entretenimento. Diferente das operadoras tradicionais que levam dias para agendar uma visita técnica, o IPTV da <strong>P2 PRO</strong> é ativado em tempo real.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Neste guia de ação rápida, vamos direto ao ponto. Vamos mostrar o caminho mais curto entre você e milhares de canais, filmes e séries em 4K. Não importa se você está no celular, no computador ou na frente da sua Smart TV agora mesmo. O futuro da televisão não espera, e nós vamos te ensinar a alcançá-lo antes mesmo de terminar de ler este parágrafo.
          </p>
        </div>
      )
    },
    {
      title: "2. O Kit de Sobrevivência para o Primeiro Acesso",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Smartphone className="w-6 h-6 text-electric" /> Dispositivo Pronto</h4>
              <p className="text-sm text-muted-foreground">Certifique-se de que seu aparelho está logado na sua conta da loja de apps (Play Store, App Store ou LG Content Store).</p>
            </div>
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Wifi className="w-6 h-6 text-amber-500" /> Conexão Testada</h4>
              <p className="text-sm text-muted-foreground">Um teste rápido no 'Fast.com' deve mostrar pelo menos 15Mbps para uma experiência sem engasgos no início.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. O Passo a Passo da Ativação Imediata",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">Siga estas três etapas simples para ter o sinal funcionando agora:</p>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">1</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Chame no WhatsApp da P2 PRO</h4>
              <p className="text-muted-foreground">Diga: 'Quero testar o IPTV na minha TV [Modelo]'. Nossa equipe enviará o link do app e seus dados em menos de 2 minutos.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">2</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Configure o Player</h4>
              <p className="text-muted-foreground">Abra o aplicativo, escolha 'Login with Xtream Codes' e digite os dados exatamente como recebeu. Respeite letras maiúsculas e minúsculas.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Dicas Extras para uma Estreia de Sucesso",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Para garantir que sua primeira noite com o novo IPTV seja perfeita, anote estas dicas:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Reinicie seu Roteador</h5>
                <p className="text-sm text-muted-foreground">Uma limpeza no cache do roteador antes de começar ajuda a estabilizar a rota de dados para os novos servidores.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Verifique a Categoria VOD</h5>
                <p className="text-sm text-muted-foreground">Não olhe só os canais. Vá na aba de 'Filmes e Séries' e veja a velocidade de carregamento dos lançamentos em 4K.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Info className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Teste Diferentes Qualidades</h5>
                <p className="text-sm text-muted-foreground">Abra o mesmo canal em HD e depois em 4K. Entenda qual deles se adapta melhor ao seu Wi-Fi para evitar surpresas no meio do uso.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Guias IPTV"
        title="Como Começar a Utilizar IPTV Hoje: Guia de Ação Rápida"
        subtitle="Não espere pelo técnico. Aprenda como liberar milhares de canais na sua TV em menos de 10 minutos agora."
        heroImage="https://images.unsplash.com/photo-1550009158-9ebf69173e03?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "Se você está lendo isso, provavelmente já cansou da burocracia das operadoras de TV tradicionais. A boa notícia é que você não precisa mais delas. O mundo mudou, e a forma de consumir conteúdo também. Começar a utilizar IPTV hoje é uma das experiências mais libertadoras para quem gosta de entretenimento de qualidade, e o melhor: você pode fazer isso sozinho, agora mesmo.",
          "Neste guia prático da P2 PRO, eliminamos toda a 'encheção de linguiça'. Vamos direto ao que interessa: como conseguir seu login, qual app baixar e como garantir que a imagem não trave. Nossa missão é que você termine esta leitura e, em menos de 10 minutos, já esteja navegando pela maior grade de programação do Brasil.",
          "Esqueça os manuais complexos e os termos técnicos que ninguém entende. Aqui a linguagem é clara e o foco é no resultado. Vamos transformar sua Smart TV em um portal de entretenimento de elite agora?"
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="🚀 Quer Começar Agora Mesmo?"
        ctaDescription="Clique no botão abaixo e fale com nossa equipe. Seu teste gratuito será ativado em menos de 60 segundos."
        ctaButtonText="Ativar Meu IPTV Hoje"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/como-comecar-utilizar-iptv-hoje")({
  head: () => ({
    meta: [
      { title: "Como Começar a Utilizar IPTV Hoje: Passo a Passo Rápido" },
      { name: "description", content: "Aprenda como configurar seu IPTV em minutos. Guia prático de instalação, login e primeiros passos para assistir hoje mesmo." },
    ],
  }),
  component: ArticlePage,
});
